import gsap from "../../helpers/gsapInstance";
import {ScrollDirectionEnum} from "../../types";
import AsyncHelper from "../../helpers/AsyncHelper";
import MainPageCommonService from "../../services/main-page/MainPageCommonService";
import MainPageCardsService from "../../services/main-page/MainPageCardsService";
import MainPageFirstScreenDesktop from "./screens/first/MainPageFirstScreenDesktop";
import MainPageThirdScreenCommon from "./screens/third/MainPageThirdScreenCommon";
import MainPageFourthScreenDesktop from "./screens/fourth/MainPageFourthScreenDesktop";
import MainPageFifthScreenDesktop from "./screens/fifth/MainPageFifthScreenDesktop";
import MainPageSecondScreenDesktop from "./screens/second/MainPageSecondScreenDesktop";

export default class MainPageDesktop extends MainPageCommonService {
    private readonly $holder: JQuery;

    private CardsService: MainPageCardsService;
    private FirstScreen: MainPageFirstScreenDesktop;
    private SecondScreen: MainPageSecondScreenDesktop;
    private ThirdScreen: MainPageThirdScreenCommon;
    private FourthScreen: MainPageFourthScreenDesktop;
    private FifthScreen: MainPageFifthScreenDesktop;

    private readonly $headerContainer: JQuery;
    private readonly $nearestEventsSection: JQuery;

    // Туториал по экранам:
    // У нас есть screens (first,second,....), это сами секции, названы абстрактно в виде числа, чтобы можно было их легко переназвать в index.blade.php
    // Все экраны вынесены в отдельные файлы для удобства

    private readonly $screens: JQuery;

    private isCanStartingScroll: boolean = false;
    private isScrollableSection: boolean = false;
    private direction: ScrollDirectionEnum;
    private activeScreen: number = 0;
    private readonly screensMaxIndex: number = 1;

    private readonly HEADER_HIDE_CLASS: string = "main-page--hide";

    private readonly selectors = {
        headerContainer: ".js-header",
        mainContainer: ".js-main-page",
        section: ".js-main-page-section",
        nearestEventsSection: ".js-main-page-nearest-events-section",
    };

    constructor($holder: JQuery) {
        super();

        this.$holder = $holder;

        this.$headerContainer = this.$holder.find(this.selectors.headerContainer);
        this.$nearestEventsSection = this.$holder.find(this.selectors.nearestEventsSection);

        this.$screens = this.$holder.find(this.selectors.section);
        this.screensMaxIndex = this.$screens.last().index();

        this.CardsService = new MainPageCardsService(this.$holder);
        this.FirstScreen = new MainPageFirstScreenDesktop(this.$holder);
        this.SecondScreen = new MainPageSecondScreenDesktop(this.$holder);
        this.FirstScreen.$secondScreenContainer = this.SecondScreen.$screenContainer;
        this.ThirdScreen = new MainPageThirdScreenCommon(this.$holder);
        this.FourthScreen = new MainPageFourthScreenDesktop(this.$holder);
        this.FifthScreen = new MainPageFifthScreenDesktop(this.$holder);

        this.setHeightCharacteristics = this.setHeightCharacteristics.bind(this);
        this.resizeScreen = this.resizeScreen.bind(this);

        this.handleWheel = this.handleWheel.bind(this);
        this.handleDirection = this.handleDirection.bind(this);
        this.scrollDownHandler = this.scrollDownHandler.bind(this);
        this.scrollUpHandler = this.scrollUpHandler.bind(this);
        this.changeNextScreen = this.changeNextScreen.bind(this);
        this.changeBackScreen = this.changeBackScreen.bind(this);

        this.initEvents = this.initEvents.bind(this);

        void this.init();
    }

    /**
     * Инициализация страницы
     * @private
     */
    private async init() {
        this.$headerContainer.addClass(this.HEADER_MAIN_PAGE_CLASS);
        this.$body.addClass(this.HIDDEN_CLASS);
        this.scrollToTop(0.2);
        await AsyncHelper.delay(0.2, true);

        // TODO: строки ниже для дебага (скролл сразу до 5 экрана)
        // gsap.to(window, {
        //     scrollTo: {
        //         y: window.innerHeight * 3,
        //         autoKill: false,
        //     },
        //     duration: 0.2
        // })
        // await this.changeFifthToScrollableScreens();
        // this.activeScreen = 5;

        this.initEvents();

        // делаем в начале инициализации страницы задержку на секунду, чтобы прогрузились все анимашки
        await AsyncHelper.delay(1, true);
        this.isCanStartingScroll = true;
    }

    /**
     * Инициализация событий на странице
     * @private
     */
    private initEvents(): void {
        this.resizeScreen();
        window.addEventListener('resize', this.resizeScreen);

        /* Должно работать только на десктопе, отрубаем в будущем поведение на планшете и мобилке */
        window.addEventListener('wheel', (event: WheelEvent) => {
            event.stopImmediatePropagation();

            if (this.isScrollableSection || !this.isCanStartingScroll) return;

            this.handleWheel(event);
        }, {passive: true});

        window.addEventListener('scroll', async (event) => {
            const nearestEventsSectionTop = this.$nearestEventsSection.offset()?.top - this.headerHeight;
            const scrollY = window.scrollY;

            if (this.activeScreen === 5 && this.isScrollableSection) {
                if (scrollY < nearestEventsSectionTop) {
                    this.isScrollableSection = false;
                    this.$body.addClass(this.HIDDEN_CLASS);
                    await this.changeBackScreen();
                }
            }
        });

        this.ThirdScreen.initEvents();
    }

    /**
     * Callback обработки события wheel (движения колеса мыши)
     * @private
     */
    private handleWheel(event: WheelEvent | MouseWheelEvent): void {
        this.isCanStartingScroll = false;

        const direction: ScrollDirectionEnum = event.deltaY > 0 ? ScrollDirectionEnum.DOWN : ScrollDirectionEnum.UP;
        this.direction = direction;

        this.handleDirection(direction)
    }

    /**
     * После handleWheel мы проверяем direction и вызываем нужный callback
     * @private
     */
    private handleDirection(direction: ScrollDirectionEnum) {
        switch (direction) {
            case ScrollDirectionEnum.DOWN:
                this.scrollDownHandler();
                break;
            case ScrollDirectionEnum.UP:
                this.scrollUpHandler();
                break;
        }
    }

    /**
     * При движении вниз проверяем на конец всех секций снизу, если возможно переключать экраны, то включаем следующий экран
     * @private
     */
    private scrollDownHandler(): void {
        if (this.activeScreen === this.screensMaxIndex) {
            this.isCanStartingScroll = true;
            return;
        }

        void this.changeNextScreen();
    }

    /**
     * Включить следующий скрин при движении ВНИЗ / ScrollDirectionEnum.DOWN
     * @private
     */
    private async changeNextScreen(): Promise<void> {
        const isCardsWasAnimated = await this.setNextScreenCardsAnimation();
        if (isCardsWasAnimated) return;

        this.isCanStartingScroll = false;

        const nextIndex = this.activeScreen + 1;

        switch (nextIndex) {
            case 1: {
                await this.changeFirstToSecondScreen();
                break;
            }
            case 2: {
                await this.changeSecondToThirdScreen();
                break;
            }
            case 3: {
                await this.changeThirdToFourthScreen();
                break;
            }
            case 4: {
                await this.changeFourthToFifthScreen();
                break;
            }
            case 5: {
                await this.changeFifthToScrollableScreens();
                break;
            }
        }

        if (nextIndex <= this.screensMaxIndex) {
            this.activeScreen = nextIndex;
        }

        this.isCanStartingScroll = true;
    }

    /**
     * Выполняем анимации с карточками по движению ВНИЗ / ScrollDirectionEnum.DOWN.
     * Функция showNextCard в самом низу
     * @private
     */
    private async setNextScreenCardsAnimation(): Promise<boolean> {
        const isSecondScreenActive = this.activeScreen === 1;
        const isFifthScreenActive = this.activeScreen === 4;

        if (isSecondScreenActive && this.CardsService.activeCardIndex < this.SecondScreen.$cards.length) {
            const cards = this.SecondScreen.$cards;
            await this.CardsService.showNextCard(cards);

            this.isCanStartingScroll = true;
            return true;
        }

        if (isFifthScreenActive && this.CardsService.activeCardIndex < this.FifthScreen.$cards.length) {
            const cards = this.FifthScreen.$cards;
            await this.CardsService.showNextCard(cards);

            this.isCanStartingScroll = true;
            return true;
        }

        return false;
    }

    /**
     * Выполняем анимации переключения экрана с 1 на 2 по движению ВНИЗ / ScrollDirectionEnum.DOWN.
     * @private
     */
    private async changeFirstToSecondScreen(): Promise<void> {
        this.FirstScreen.setMainPointAnimation(ScrollDirectionEnum.DOWN);

        gsap.to(window, {
            scrollTo: {
                y: this.SecondScreen.$screen.get(0),
                autoKill: false
            },
            duration: this.ANIMATION_DURATION_IN_SECONDS
        });

        await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS, true);
    }

    /**
     * Выполняем анимации переключения экрана со 2 на 3 по движению ВНИЗ / ScrollDirectionEnum.DOWN.
     * @private
     */
    private async changeSecondToThirdScreen(): Promise<void> {
        const tl = gsap.timeline();

        const {
            $cards: $secondScreenCards,
            $topContent: $secondScreenTopContent,
            $bottomContent: $secondScreenBottomContent,
            $innerCenterPoint: $secondScreenCenterPoint,
            SCALE_COEFFICIENT: secondScreenScaleCoefficient
        } = this.SecondScreen;

        tl.addLabel('START_CHANGE_SCREEN')
            .addLabel('HIDE_SECOND_AND_FIRST_SCREENS_CONTENT')
            .to([
                $secondScreenCards,
                $secondScreenTopContent,
                $secondScreenBottomContent,
                this.FirstScreen.$mainPoint
            ], {
                opacity: 0,
                duration: this.QUARTER_ANIMATION_DURATION * 2
            })
            .addLabel('SECOND_SCREEN_SCALE_CENTER_POINT')
            .to($secondScreenCenterPoint, {
                duration: this.QUARTER_ANIMATION_DURATION * 2,
                scale: secondScreenScaleCoefficient
            })
            .addLabel('END');

        await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS, true);

        this.SecondScreen.$screen.hide();
        this.CardsService.activeCardIndex = 0;

        this.ThirdScreen.$screen.show();
        await this.ThirdScreen.setDefaultProps();
        this.ThirdScreen.$screenContainer.css({opacity: 1});
        await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS / 2, true);
        await this.ThirdScreen.setStartAnimationForPoints();
    }

    /**
     * Выполняем анимации переключения экрана с 3 на 4 по движению ВНИЗ / ScrollDirectionEnum.DOWN.
     * @private
     */
    private async changeThirdToFourthScreen(): Promise<void> {
        this.FourthScreen.setDefaultCirclesValues();

        gsap.to(window, {
            scrollTo: {
                y: this.FourthScreen.$screen.get(0),
                autoKill: false
            },
            duration: this.ANIMATION_DURATION_IN_SECONDS
        });

        await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS, true);

        await this.FourthScreen.setStartedCirclesAnimation();
    }

    /**
     * Выполняем анимации переключения экрана с 4 на 5 по движению ВНИЗ / ScrollDirectionEnum.DOWN.
     * @private
     */
    private async changeFourthToFifthScreen(): Promise<void> {
        await this.FourthScreen.setHideScreenAnimation();
        this.FourthScreen.$screen.hide();

        this.CardsService.activeCardIndex = 0;

        this.FifthScreen.setDefaultProps();
        this.FifthScreen.$screen.show();
        await this.FifthScreen.setShowScreenAnimation();
    }

    /**
     * Выполняем анимации переключения экрана с 5 на scrollable-screens (нижние экраны без анимаций) по движению ВНИЗ / ScrollDirectionEnum.DOWN.
     * @private
     */
    private async changeFifthToScrollableScreens(): Promise<void> {
        this.isScrollableSection = true;

        gsap.to(window, {
            scrollTo: {
                y: this.$nearestEventsSection.get(0),
            },
            duration: this.ANIMATION_DURATION_IN_SECONDS,
        });

        await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS, true);

        this.$body.removeClass(this.HIDDEN_CLASS);
    }

    /**
     * При движении вверх проверяем на конец всех секций сверху, если возможно переключать экраны, то включаем следующий экран
     * @private
     */
    private scrollUpHandler(): void {
        if (this.activeScreen === 0) {
            this.isCanStartingScroll = true;
            return;
        }

        void this.changeBackScreen();
    }

    /**
     * Включить следующий скрин при движении ВВЕРХ / ScrollDirectionEnum.UP
     * @private
     */
    private async changeBackScreen(): Promise<void> {
        const isCardsWasAnimated = await this.setBackScreenCardsAnimation();
        if (isCardsWasAnimated) return;

        this.isCanStartingScroll = false;

        const backIndex = this.activeScreen - 1;

        switch (backIndex) {
            case 0: {
                await this.changeSecondToFirstScreen();
                break;
            }
            case 1: {
                await this.changeThirdToSecondScreen();
                break;
            }
            case 2: {
                await this.changeFourthToThirdScreen();
                break;
            }
            case 3: {
                await this.changeFifthToFourthScreen();
                break;
            }
            case 4: {
                await this.changeScrollableScreensToFifthScreen();
                break;
            }
        }

        this.activeScreen = backIndex;
        this.isCanStartingScroll = true
    }

    /**
     * Выполняем анимации с карточками по движению ВВЕРХ / ScrollDirectionEnum.UP.
     * Функция hidePreviousCard в самом низу
     * @private
     */
    private async setBackScreenCardsAnimation(): Promise<boolean> {
        const isSecondScreenActive = this.activeScreen === 1;
        const isFifthScreenActive = this.activeScreen === 4;
        const isNotFirstCard = this.CardsService.activeCardIndex > 0;

        if (isSecondScreenActive && isNotFirstCard) {
            const cards = this.SecondScreen.$cards;
            await this.CardsService.hidePreviousCard(cards);

            this.isCanStartingScroll = true;
            return true;
        }

        if (isFifthScreenActive && isNotFirstCard) {
            const cards = this.FifthScreen.$cards;
            await this.CardsService.hidePreviousCard(cards);

            this.isCanStartingScroll = true;
            return true;
        }

        return false;
    }

    /**
     * Выполняем анимации переключения экрана со 2 на 1 по движению ВВЕРХ / ScrollDirectionEnum.UP.
     * @private
     */
    private async changeSecondToFirstScreen(): Promise<void> {
        this.FirstScreen.setMainPointAnimation(ScrollDirectionEnum.UP);

        this.$headerContainer.addClass(this.HEADER_HIDE_CLASS);

        gsap.to(window, {
            scrollTo: {
                y: 0,
                autoKill: false
            },
            duration: this.ANIMATION_DURATION_IN_SECONDS
        });

        await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS, true);
        this.$headerContainer.removeClass(this.HEADER_HIDE_CLASS);
    }

    /**
     * Выполняем анимации переключения экрана с 3 на 2 по движению ВВЕРХ / ScrollDirectionEnum.UP.
     * @private
     */
    private async changeThirdToSecondScreen(): Promise<void> {
        const $thirdScreenContainer = this.ThirdScreen?.$screenContainer;
        if ($thirdScreenContainer?.length) {
            gsap.to($thirdScreenContainer, {
                opacity: 0,
                duration: 0.5
            })
        }
        await AsyncHelper.delay(0.5, true);

        this.ThirdScreen.$screen.hide();
        this.SecondScreen.$screen.show();

        const {
            $cards: $secondScreenCards,
            $topContent: $secondScreenTopContent,
            $bottomContent: $secondScreenBottomContent,
            $innerCenterPoint: $secondScreenCenterPoint,
            STARTED_SCALE_COEFFICIENT: secondScreenStartedScaleCoefficient
        } = this.SecondScreen;

        this.CardsService.activeCardIndex = $secondScreenCards.length;

        const tl = gsap.timeline();

        tl.addLabel('START')
            .addLabel('SCALE_CENTER_POINT')
            .to($secondScreenCenterPoint, {
                duration: this.QUARTER_ANIMATION_DURATION * 2,
                scale: secondScreenStartedScaleCoefficient
            })
            .addLabel('VISIBLE_CARDS_CONTENT')
            .to($secondScreenCards, {
                opacity: 1,
                duration: 0
            })
            .to([
                $secondScreenTopContent,
                $secondScreenBottomContent,
                this.FirstScreen.$mainPoint
            ], {
                opacity: 1,
                duration: this.QUARTER_ANIMATION_DURATION * 2
            })
            .addLabel('END');

        await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS, true);
    }

    /**
     * Выполняем анимации переключения экрана с 4 на 3 по движению ВВЕРХ / ScrollDirectionEnum.UP.
     * @private
     */
    private async changeFourthToThirdScreen(): Promise<void> {
        gsap.to(window, {
            scrollTo: {
                y: this.ThirdScreen.$screen.get(0),
                autoKill: false
            },
            duration: this.ANIMATION_DURATION_IN_SECONDS
        });

        await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS, true);
    }

    /**
     * Выполняем анимации переключения экрана с 5 на 4 по движению ВВЕРХ / ScrollDirectionEnum.UP.
     * @private
     */
    private async changeFifthToFourthScreen(): Promise<void> {
        await this.FifthScreen.setHideScreenAnimation();
        this.FifthScreen.$screen.hide();

        this.FourthScreen.$screen.show();
        await this.FourthScreen.setShowScreenAnimation();
    }

    /**
     * Выполняем анимации переключения экрана с scrollable-screens (нижние экраны без анимаций) на 5 по движению ВНИЗ / ScrollDirectionEnum.DOWN.
     * @private
     */
    private async changeScrollableScreensToFifthScreen(): Promise<void> {
        if (!this.$body.hasClass(this.HIDDEN_CLASS)) {
            this.$body.addClass(this.HIDDEN_CLASS);
        }

        gsap.to(window, {
            scrollTo: {
                y: this.FifthScreen.$screen.get(0),
                autoKill: false
            },
            duration: this.ANIMATION_DURATION_IN_SECONDS
        });

        await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS, true);
    }

    /**
     * Callback для resize у страницы
     * @private
     */
    private resizeScreen(): void {
        this.setHeightCharacteristics();
        this.FirstScreen.setMainPointOffset();
        this.CardsService.setCardsOffset();
    }
}
